import React from "react";
import {
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  Modal,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
} from "@mui/material";
import ViewCSVData from "./ViewCSVData";
import CSVUploader from "./CSVUploader";
import {
  setCSVBatchData,
  setIsAlbum,
  setSelectedItems,
  submitSuccessfully,
} from "../../redux/slices/contentSlice/contentForm/contentForm";
import { useDispatch, useSelector } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadBox from "./UploadBox";
import { useState } from "react";
import UploadFile from "./UploadFile";
import { v4 as uuidv4 } from "uuid";
import FilePreview from "./FilePreview";
import { LoadingButton } from "@mui/lab";
import useUploadMasterFile from "../../hooks/useUploadMasterFile";
import useHandleFilePath from "../../hooks/useHandleFilePath";
import { createContent } from "../../graphql/mutations";
import { toast } from "react-toastify";
import { generateClient } from "aws-amplify/api";
import AudioFileSelector from "./AudioSelector";
import LinearProgress from "@mui/material/LinearProgress";
import OneDrivePicker from "./OneDrivePicker2";
import Papa from "papaparse";
import { urlToFile } from "../../helper/onedrive/urlToFile";
import { useEffect, useRef } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { handleComplianceOpen } from "../../redux/slices/contentSlice/contentForm/contentForm";
import ComplianceModal from "./ComplianceModal";
import useCategorizeFiles from "../../hooks/useCategorizeFiles";
import { useCallback } from "react";
import { resetOnedrive } from "../../redux/slices/contentSlice/onedriveContent/onedriveContent";
import { validateCSVHeaders } from "../../helper/csvHeaders";
import { uploadData } from "aws-amplify/storage";

const UploadMultipleSongs = ({ formik }) => {
  const client = generateClient();
  const dispatch = useDispatch();
  const csvBatchData = useSelector((state) => state.contentForm.csvBatchData);
  const selectedItems = useSelector((state) => state.contentForm.selectedItems) || [];
  const contentForm = useSelector((state) => state.contentForm);
  const onedriveFiles = useSelector((state) => state.oneDriveContent.files) || [];

  const [uploadProgress, setUploadProgress] = useState(0);
  const [audioFiles, setAudioFiles] = useState([]);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);
  const [mappings, setMappings] = useState({});
  const [open, setOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const handleClose = () => setOpen(false);
  const [masterFileContent, setMasterFileContent] = useState({
    filePath: "",
    file: "",
  });
  const complianceOpen = useSelector(
    (state) => state.contentForm.complianceOpen
  );
  const complianceChecked = useSelector(
    (state) => state.contentForm.complianceChecked
  );
  const agreementForm = useSelector((state) => state.agreementForm);
  
  // Track if initial data has been processed to prevent infinite loops
  const initialProcessingDone = useRef(false);
  const audioFilesProcessed = useRef(false);
  const dispatchInProgress = useRef(false);
  const selectedItemsRef = useRef(selectedItems);
  
  // Include thumbnailFiles in the files to categorize
  const allFiles = onedriveFiles?.length 
    ? onedriveFiles 
    : [...(audioFiles || []), ...(thumbnailFiles || [])];
    
  const { videos, images, pdfs, csv, audios } = useCategorizeFiles(
    allFiles,
    onedriveFiles?.length ? "onedrive" : "local"
  );

  const [isLoading, setLoading] = useState(false);

  // Keep ref updated with latest selected items
  useEffect(() => {
    selectedItemsRef.current = selectedItems;
  }, [selectedItems]);

  const { handleFilePath } = useHandleFilePath(
    contentForm,
    csvBatchData,
    selectedItems
  );

  // Handle thumbnail file selection
  const handleThumbnailSelect = (event) => {
    const files = Array.from(event.target.files);
    setThumbnailFiles(files);
  };

  // Handler for mapping thumbnails to audio files
  const handleMappingChange = (audioName, thumbnailIndex) => {
    const newMappings = { ...mappings };
    newMappings[audioName] = thumbnailFiles[thumbnailIndex];
    setMappings(newMappings);
  };

  // Generate a thumbnail path based on audio file
  const generateThumbnailPath = (audioFile, thumbnailFile) => {
    if (!audioFile || !thumbnailFile) return "";
    
    const fileExtension = thumbnailFile.name.split(".").pop().toLowerCase();
    const guid = uuidv4();
    let title = audioFile.name.replace(/\.[^/.]+$/, ""); // Remove extension
    
    // Clean the title for safe use in path
    title = title.replace(/[^a-zA-Z0-9]/g, "");
    
    const filePath = `${contentForm?.contentType?.replace(/\s+/g, "") || "LISTEN"}/${contentForm?.category?.replace(
      /\s+/g, ""
    ) || "Music"}/${title}/${guid}.${fileExtension}`;
    
    return filePath;
  };

  // Updates selected items in redux, but with additional safeguards
  const safeDispatchSelectedItems = useCallback((item) => {
    // Prevent dispatching if already in progress
    if (dispatchInProgress.current) return;
    
    dispatchInProgress.current = true;
    
    setTimeout(() => {
      dispatch(setSelectedItems(item));
      dispatchInProgress.current = false;
    }, 0);
  }, [dispatch]);

  // Batch update selected items
  const batchUpdateSelectedItems = useCallback((items) => {
    // Skip if already dispatching 
    if (dispatchInProgress.current) return;
    
    dispatchInProgress.current = true;
    
    // Clear existing items first
    dispatch(setSelectedItems([]));
    
    // Create a promise chain for each dispatch to ensure they happen in sequence
    let chain = Promise.resolve();
    
    items.forEach(item => {
      chain = chain.then(() => {
        return new Promise(resolve => {
          setTimeout(() => {
            dispatch(setSelectedItems(item));
            resolve();
          }, 0);
        });
      });
    });
    
    chain.finally(() => {
      dispatchInProgress.current = false;
    });
    
  }, [dispatch]);

  // Clear selected items and re-build them with proper mappings
  const updateSelectedItems = useCallback(() => {
    // Skip if no audio files or CSV data
    if (!audios?.length || !csvBatchData?.title?.length) return;
    
    // Skip if dispatch already in progress
    if (dispatchInProgress.current) return;
    
    // Only process as many files as we have CSV data for
    const validFiles = audios.slice(0, csvBatchData.title.length);
    const newItems = [];
    
    validFiles.forEach((file, index) => {
      const title = csvBatchData?.title?.[index]?.value;
      const { filePath } = handleFilePath(file, title);
      
      // Build selected item with thumbnail information if available
      const selectedItem = {
        filePath,
        masterFile: file?.downloadUrl || URL.createObjectURL(file),
        type: file?.type,
      };
      
      // If a thumbnail is mapped to this audio file, include it
      if (mappings[file.name]) {
        const thumbnailFile = mappings[file.name];
        const thumbnailPath = generateThumbnailPath(file, thumbnailFile);
        selectedItem.posters = [thumbnailPath];
        selectedItem.posterFile = URL.createObjectURL(thumbnailFile);
      }
      
      newItems.push(selectedItem);
    });
    
    // Deep compare new items with current selectedItems
    const currentItems = selectedItemsRef.current || [];
    const needsUpdate = newItems.length !== currentItems.length || 
      JSON.stringify(newItems) !== JSON.stringify(currentItems);
    
    // Only update if there's actually a change to avoid infinite loops
    if (needsUpdate) {
      batchUpdateSelectedItems(newItems);
    }
  }, [audios, csvBatchData, handleFilePath, mappings, batchUpdateSelectedItems]);

  const { uploadMasterFile } = useUploadMasterFile();

  // Function to upload thumbnails
  const uploadThumbnail = async (thumbnailFile, thumbnailPath) => {
    if (!thumbnailFile || !thumbnailPath) return null;
    
    try {
      // Convert blob URL to File if needed
      let fileToUpload = thumbnailFile;
      if (typeof thumbnailFile === 'string' && thumbnailFile.startsWith('blob:')) {
        const response = await fetch(thumbnailFile);
        const blob = await response.blob();
        fileToUpload = new File([blob], thumbnailPath.split('/').pop(), { type: blob.type });
      }
      
      // Upload the thumbnail to S3
      await uploadData({
        key: thumbnailPath,
        data: fileToUpload
      }).result;
      
      return thumbnailPath;
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      return null;
    }
  };

  // Validate that CSV data and audio files are properly matched
  const validateUpload = () => {
    const errors = [];
    
    if (!csvBatchData || !csvBatchData.title) {
      errors.push("No CSV data loaded. Please upload a CSV file with song metadata.");
      return errors;
    }
    
    if (!audios || audios.length === 0) {
      errors.push("No audio files selected. Please select audio files to upload.");
      return errors;
    }
    
    if (csvBatchData.title.length !== audios.length) {
      errors.push(`CSV data (${csvBatchData.title.length} rows) doesn't match number of audio files (${audios.length}). Please ensure they match.`);
    }
    
    // Check for missing required fields in CSV data
    csvBatchData.title.forEach((title, index) => {
      if (!title || !title.value) {
        errors.push(`Missing title for song at row ${index + 1} in CSV.`);
      }
    });
    
    return errors;
  };

  const handleSubmitSeason = () => {
    if (!complianceChecked) {
      dispatch(handleComplianceOpen(true));
      return;
    }

    // Validate before submitting
    const errors = validateUpload();
    if (errors.length > 0) {
      setValidationErrors(errors);
      errors.forEach(error => toast.error(error));
      return;
    }
    
    setValidationErrors([]);

    if (selectedItems.length > 0) {
      let uploadedCount = 0;
      let failedCount = 0;
      setLoading(true);

      selectedItems.forEach(async (item, index) => {
        try {
          // Make sure we have the necessary data
          if (!csvBatchData?.title?.[index]?.value) {
            throw new Error(`Missing title for song at index ${index}`);
          }

          await uploadMasterFile(item);
          
          // Upload thumbnail if available
          let posterPaths = [];
          if (item.posterFile && item.posters && item.posters.length > 0) {
            const thumbnailPath = await uploadThumbnail(item.posterFile, item.posters[0]);
            if (thumbnailPath) {
              posterPaths = [thumbnailPath];
            }
          }
          
          // Ensure we have values for all required fields, using defaults if needed
          const content = {
            agreementId: agreementForm?.id,
            title: csvBatchData?.title?.[index]?.value,
            type: contentForm.contentType,
            category: contentForm.category,
            composer: csvBatchData?.composer?.[index]?.value || "",
            duration: csvBatchData?.duration?.[index]?.value || 0,
            filePath: item.filePath,
            trailerFilePath: "",
            language: "eng",
            year: csvBatchData?.year?.[index]?.value || new Date().getFullYear(),
            posters: posterPaths.length > 0 ? posterPaths : item.posters || [],
            genre: csvBatchData?.genre?.[index]?.value
              ? csvBatchData?.genre?.[index]?.value.split(",").map((genre) => genre.trim())
              : ["Unspecified"],
            isDrmProtected: false,
            albumId: contentForm.albumId,
            isSeries: false,
            isGrouped: true,
          };
          
          console.log("Creating content with:", content);
          
          const response = await client.graphql({
            query: createContent,
            variables: {
              input: content,
            },
          });
          
          if (response.data.createContent) {
            uploadedCount++;
            setUploadProgress(
              Math.round((uploadedCount / selectedItems.length) * 100)
            );
            
            // Show success when all items are processed
            if (uploadedCount + failedCount === selectedItems.length) {
              if (failedCount > 0) {
                toast.warning(`Completed with ${uploadedCount} successful and ${failedCount} failed uploads.`);
              } else {
                toast.success(`Successfully uploaded all ${uploadedCount} songs!`);
              }
              
              // Use setTimeout to delay these dispatches and help break the update cycle
              setTimeout(() => {
                dispatch(submitSuccessfully(true));
                dispatch(setIsAlbum(false));
                dispatch(resetOnedrive());
              }, 0);
              
              setLoading(false);
              formik.resetForm();
            }
          }
        } catch (error) {
          console.error(`Error uploading item ${index}:`, error);
          failedCount++;
          toast.error(`Error uploading "${csvBatchData?.title?.[index]?.value || `Song ${index+1}`}": ${error.message}`);
          
          // Update progress even for failures
          setUploadProgress(
            Math.round(((uploadedCount + failedCount) / selectedItems.length) * 100)
          );
          
          // Check if we've processed all items
          if (uploadedCount + failedCount === selectedItems.length) {
            setLoading(false);
          }
        }
      });
    }
  };

  const handleBatchCSVData = useCallback((csvData) => {
    const result = validateCSVHeaders(csvData, contentForm?.contentType);
    if (!result?.isValid) {
      result?.errors?.forEach((error) => {
        toast.warning(error);
      });
    }
    
    console.log(result?.mappedHeaders, "csvBatchData");
    
    // Use setTimeout to break potential update cycles
    setTimeout(() => {
      dispatch(setCSVBatchData(result?.mappedHeaders));
    }, 0);
    
    // Reset files when CSV changes to maintain consistency
    setValidationErrors([]);
    
    // Reset the audio files processed flag when CSV changes
    audioFilesProcessed.current = false;
  }, [dispatch, contentForm?.contentType]);

  const handleAudioFileChange = useCallback((files) => {
    setAudioFiles(files);
    audioFilesProcessed.current = false; // Reset flag when files change
  }, []);

  // Effect to update selected items when dependencies change - with debounce
  useEffect(() => {
    // Debounce to prevent rapid updates
    const debounceTimer = setTimeout(() => {
      // This condition prevents infinite updates
      if (!audioFilesProcessed.current && audios?.length > 0 && csvBatchData?.title?.length > 0) {
        audioFilesProcessed.current = true;
        updateSelectedItems();
      }
    }, 100);
    
    return () => clearTimeout(debounceTimer);
  }, [csvBatchData, audios, updateSelectedItems]);

  // When mappings change, update selected items - with debounce
  useEffect(() => {
    // Skip if no files or CSV data
    if (!audios?.length || !csvBatchData?.title?.length) return;
    
    // Debounce to prevent rapid updates
    const debounceTimer = setTimeout(() => {
      // Only update if there are actually mappings
      if (Object.keys(mappings).length > 0) {
        updateSelectedItems();
      }
    }, 100);
    
    return () => clearTimeout(debounceTimer);
  }, [mappings, updateSelectedItems, audios, csvBatchData]);

  // Reset the audio files processed flag when component unmounts
  useEffect(() => {
    return () => {
      audioFilesProcessed.current = false;
      dispatchInProgress.current = false;
    };
  }, []);

  // Check if we have title data from CSV
  const hasCsvTitleData = !!(csvBatchData?.title && csvBatchData.title.length > 0);

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        Upload Multiple Songs
      </AccordionSummary>
      <Box>
        <Box display={"flex"} alignItems={"center"} gap={4}>
          <CSVUploader onFileLoad={handleBatchCSVData} />
          or
          <OneDrivePicker />
        </Box>
        {csvBatchData ? <ViewCSVData csvData={csvBatchData} /> : null}

        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">Please fix the following errors:</Typography>
            <ul style={{ marginTop: 8, paddingLeft: 16 }}>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Box display={"flex"} gap={4} flexWrap={"wrap"}>
          {csvBatchData && (
            <AudioFileSelector
              setAudioFiles={handleAudioFileChange}
              audioFiles={audioFiles}
            />
          )}
        </Box>
        
        {/* Data Validation Warning */}
        {hasCsvTitleData && audios?.length > 0 && csvBatchData.title.length !== audios.length && (
          <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
            You have {audios.length} audio files but {csvBatchData.title.length} entries in your CSV.
            Please make sure the number of audio files matches the number of CSV rows.
          </Alert>
        )}
        
        {/* Thumbnail Upload Section */}
        {audios?.length > 0 && (
          <Box mt={2} width="100%">
            <Typography variant="h6">Upload Thumbnails</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Add thumbnail images for your songs. You can map each song to a specific image in the next step.
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ mt: 1, mb: 2 }}
            >
              Select Thumbnail Images
              <input
                hidden
                type="file"
                accept="image/*"
                multiple
                onChange={handleThumbnailSelect}
              />
            </Button>
            
            {thumbnailFiles.length > 0 && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                {thumbnailFiles.length} thumbnail files selected
              </Typography>
            )}
          </Box>
        )}
        
        {/* Audio Files List */}
        <Box>
          {audios?.length > 0 && (
            <div>
              <h3>Selected Audio Files:</h3>
              <ul>
                {audios.map((file, index) => (
                  <li key={index}>{file.name}</li>
                ))}
              </ul>
            </div>
          )}
        </Box>
        
        {/* Thumbnail Mapping Section */}
        {audios?.length > 0 && thumbnailFiles.length > 0 && (
          <Box mt={2} mb={2}>
            <Typography variant="h6">Map Thumbnails to Songs</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              For each song, select a thumbnail image to be displayed alongside it.
            </Typography>
            <Grid container spacing={2}>
              {audios.map((audio, index) => (
                <Grid item xs={12} key={index}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography width={200} noWrap>{audio.name}</Typography>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Select Thumbnail</InputLabel>
                      <Select
                        label="Select Thumbnail"
                        onChange={(e) => handleMappingChange(audio.name, e.target.value)}
                        value={Object.keys(mappings).includes(audio.name) ? 
                          thumbnailFiles.findIndex(t => t === mappings[audio.name]) : 
                          ""}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {thumbnailFiles.map((thumbnail, idx) => (
                          <MenuItem key={idx} value={idx}>
                            {thumbnail.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    {/* Preview of selected thumbnail */}
                    {mappings[audio.name] && (
                      <Box ml={2} width={60} height={60} border="1px solid #ddd" borderRadius={1} overflow="hidden">
                        <img
                          src={URL.createObjectURL(mappings[audio.name])}
                          alt={`Thumbnail for ${audio.name}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        
        {selectedItems.length > 0 && (
          <Box width="100%" my={2}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="body2" color="textSecondary">
              {`${uploadProgress}%`}
            </Typography>
          </Box>
        )}
        
        {!complianceChecked && (
          <ComplianceModal
            open={complianceOpen}
            handleClose={handleClose}
            formik={formik}
          />
        )}
        
        <LoadingButton
          disabled={
            !audios?.length || 
            !csvBatchData?.title?.length || 
            audios.length !== csvBatchData?.title?.length ||
            isLoading ||
            validationErrors.length > 0
          }
          variant="contained"
          fullWidth
          onClick={handleSubmitSeason}
          loading={isLoading}
        >
          Submit Batch
        </LoadingButton>
      </Box>
    </Accordion>
  );
};

export default UploadMultipleSongs;